import { useEffect, type ReactNode } from "react";
import { cn } from "../lib/cn.js";

/**
 * Phase C.2 共用 Modal — 轻量 dialog 包装，dosia-* token + 半径 + 阴影
 * （`shadow-elev-modal` 暂未在 tailwind 配置中，所以用 `shadow-2xl` 占位；视觉决策保持
 * brand-neutral product chrome 风格）。
 *
 * 不用原生 `<dialog>`：happy-dom 测试环境对其支持参差，且 ESC / backdrop click 需要
 * 手撸。改用普通 div + role="dialog" + key 处理。
 */
export function Modal({
  open,
  onClose,
  title,
  footer,
  children,
  size = "md",
}: {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  footer?: ReactNode;
  children: ReactNode;
  size?: "sm" | "md" | "lg";
}): JSX.Element | null {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      role="presentation"
      className="fixed inset-0 z-50 flex items-center justify-center bg-dosia-bg/70 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        className={cn(
          "bg-dosia-bg-light border border-dosia-border rounded-xl shadow-2xl flex flex-col max-h-[80vh]",
          size === "sm" && "w-[320px]",
          size === "md" && "w-[480px]",
          size === "lg" && "w-[720px]",
        )}
        onClick={(e) => e.stopPropagation()}
      >
        {title ? (
          <div className="px-4 py-3 border-b border-dosia-border flex items-center justify-between">
            <div className="text-sm font-semibold text-dosia-text">{title}</div>
            <button
              type="button"
              onClick={onClose}
              className="text-dosia-text-secondary hover:text-dosia-text text-base leading-none"
              aria-label="关闭"
            >
              ×
            </button>
          </div>
        ) : null}
        <div className="flex-1 overflow-auto px-4 py-3">{children}</div>
        {footer ? (
          <div className="px-4 py-3 border-t border-dosia-border flex justify-end gap-2">
            {footer}
          </div>
        ) : null}
      </div>
    </div>
  );
}
